/** CA-75 wire conformance helper. Not connected to capture or the v1 IndexedDB outbox. */
import type { ScorePayload } from './types'

export interface OfflineWireMutation {
  clientMutationId: string
  sequence: number
  recordKey: { participantId: string; criterionId: string; sessionId: string; activityId: string | null }
  baseRevision: string | null
  predecessorMutationId: string | null
  configurationRevision: string
  capturedAt: string
  payload: ScorePayload
}
export interface OfflineWireBatch {
  schemaVersion: 1
  snapshotId: string
  deviceId: string
  dataEpoch: string
  mutations: OfflineWireMutation[]
}

function canonical(value: unknown): string {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return JSON.stringify(value)
  if (typeof value === 'number' && Number.isFinite(value)) return JSON.stringify(value)
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`
  if (value && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    const record = value as Record<string, unknown>
    return `{${Object.keys(record).sort().map(key => `${JSON.stringify(key)}:${canonical(record[key])}`).join(',')}}`
  }
  throw new Error('Offline command must contain plain finite JSON values')
}

/** Hash identity includes the authenticated actor and event; never accept these from an untrusted response. */
export function canonicalOfflineCommand(batch: OfflineWireBatch, mutation: OfflineWireMutation, actorId: string, eventId: string): string {
  if (batch.schemaVersion !== 1 || !Number.isSafeInteger(mutation.sequence) || mutation.sequence < 1) throw new Error('Invalid offline wire version or sequence')
  return canonical({
    schemaVersion: 1, actorId, eventId,
    clientMutationId: mutation.clientMutationId, deviceId: batch.deviceId, snapshotId: batch.snapshotId,
    dataEpoch: batch.dataEpoch, sequence: mutation.sequence,
    recordKey: {
      eventId, participantId: mutation.recordKey.participantId, staffId: actorId,
      criterionId: mutation.recordKey.criterionId, sessionId: mutation.recordKey.sessionId,
      activityId: mutation.recordKey.activityId ?? '0', episodeId: '0',
    },
    baseRevision: mutation.baseRevision, predecessorMutationId: mutation.predecessorMutationId ?? null,
    configurationRevision: mutation.configurationRevision, capturedAt: mutation.capturedAt,
    payload: {
      scoringMode: mutation.payload.scoringMode, score: mutation.payload.score,
      ...(mutation.payload.counter !== undefined ? { counter: mutation.payload.counter } : {}),
      ...(mutation.payload.buckets !== undefined ? { buckets: mutation.payload.buckets } : {}),
      descriptorSelections: mutation.payload.descriptorSelections.map(selection => ({
        categoryId: selection.categoryId, itemId: selection.itemId, levelValue: selection.levelValue,
      })),
    },
  })
}

export async function hashOfflineCommand(batch: OfflineWireBatch, mutation: OfflineWireMutation, actorId: string, eventId: string): Promise<string> {
  const encoded = new TextEncoder().encode(canonicalOfflineCommand(batch, mutation, actorId, eventId))
  const digest = await crypto.subtle.digest('SHA-256', encoded)
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('')
}
